/**
 * OnboardingWizardPanel
 *
 * Renders inside `/admin/analytics?tab=journeys` (see JourneysTab.jsx).
 * Reads `GET /api/admin/analytics/onboarding-wizard` and surfaces the
 * funnel, drop-off points, completion rate, field-fill rates, and a
 * spot-check of the 20 most recent events.
 */
import { useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Loader2 } from "lucide-react";

const sectionLabel = (key) => {
  // Convert "s1_screen2_view" -> "S1 · screen 2 · view"
  const m = key.match(/^s(\d+)_screen(\d+)_(\w+)$/);
  if (!m) return key;
  return `S${m[1]} · screen ${m[2]} · ${m[3]}`;
};

export default function OnboardingWizardPanel({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get(
          `/admin/analytics/onboarding-wizard?days=${days}`,
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/60 py-4" data-testid="ow-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading wizard analytics...
      </div>
    );
  }
  if (err) {
    return (
      <div className="text-red-400 text-sm py-3" data-testid="ow-panel-error">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const funnelEntries = Object.entries(data.funnel || {}).sort();
  const dropOffEntries = Object.entries(data.drop_off || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const fillEntries = Object.entries(data.field_fill_rate || {}).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <section
      className="mt-6 border border-[#C5A55A]/20 rounded-lg p-4 bg-black/30"
      data-testid="onboarding-wizard-panel"
    >
      <h2 className="text-[#C5A55A] text-lg font-semibold mb-3" data-testid="ow-panel-title">
        Onboarding Wizard ({data.window_days}d)
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Users started" value={data.unique_users_started} testid="ow-stat-started" />
        <Stat label="Completions" value={data.completions} testid="ow-stat-completed" />
        <Stat
          label="Completion rate"
          value={`${data.completion_rate_pct}%`}
          testid="ow-stat-rate"
        />
        <Stat label="Total events" value={data.total_events} testid="ow-stat-events" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Block title="Funnel" testid="ow-block-funnel">
          {funnelEntries.length === 0 ? (
            <Empty />
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {funnelEntries.map(([k, v]) => (
                  <tr key={k} className="border-b border-white/5">
                    <td className="py-1 text-white/70">{sectionLabel(k)}</td>
                    <td className="py-1 text-right text-white tabular-nums">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>

        <Block title="Drop-off (last screen before abandon)" testid="ow-block-dropoff">
          {dropOffEntries.length === 0 ? (
            <Empty />
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {dropOffEntries.map(([k, v]) => (
                  <tr key={k} className="border-b border-white/5">
                    <td className="py-1 text-white/70">{sectionLabel(k + "_x")}</td>
                    <td className="py-1 text-right text-white tabular-nums">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>

        <Block title="Field fill rate (% of submits)" testid="ow-block-fillrate">
          {fillEntries.length === 0 ? (
            <Empty />
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {fillEntries.map(([k, v]) => (
                  <tr key={k} className="border-b border-white/5">
                    <td className="py-1 text-white/70">{k}</td>
                    <td className="py-1 text-right text-white tabular-nums">{v}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>

        <Block title="Recent events" testid="ow-block-recent">
          {!data.recent?.length ? (
            <Empty />
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {data.recent.map((e, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-1 text-white/50 w-24 truncate">
                      {e.ts?.slice(5, 16).replace("T", " ")}
                    </td>
                    <td className="py-1 text-white/70 truncate">
                      S{e.section} · scr{e.screen ?? "?"} · {e.event}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>
      </div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-md p-3" data-testid={testid}>
      <div className="text-[#C5A55A] text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-white/55 text-xs">{label}</div>
    </div>
  );
}
function Block({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="text-white/80 text-sm font-medium mb-2">{title}</h3>
      {children}
    </div>
  );
}
function Empty() {
  return <p className="text-white/40 text-xs italic">No events in this window.</p>;
}
